import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { sk } from "date-fns/locale";
import { toast } from "sonner";
import { CalendarDays, Clock, Check, ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Calendar } from "@/components/ui/calendar";
import GdprConsent from "@/components/GdprConsent";
import { api } from "@/lib/api";

const SLOTS = [
  { key: "morning", label: "Doobeda", time: "8:00 – 12:00" },
  { key: "afternoon", label: "Poobede", time: "12:00 – 16:00" },
  { key: "evening", label: "Podvečer", time: "16:00 – 19:00" },
];

export default function Rezervacia() {
  const [params] = useSearchParams();
  const leadId = params.get("lead") || "";
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({
    name: params.get("name") || "",
    email: params.get("email") || "",
    phone: "", address: "", note: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [gdpr, setGdpr] = useState(false);

  useEffect(() => {
    document.title = "Rezervácia obhliadky — Zateplenia.eu";
    window.scrollTo(0, 0);
    api.get("/booking/blocked-dates").then((r) => setBlockedDates(r.data?.dates || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const blockedDateObjs = blockedDates.map((d) => { const [y, m, day] = d.split("-").map(Number); return new Date(y, m - 1, day); });
  const canSubmit = date && slot && form.name.trim() && form.email.trim() && gdpr;

  const submit = async () => {
    if (!date || !slot || !form.name.trim() || !form.email.trim()) {
      toast.error("Vyberte dátum, čas a vyplňte meno a e-mail.");
      return;
    }
    if (!gdpr) {
      toast.error("Potvrďte súhlas so spracovaním osobných údajov.");
      return;
    }
    setSending(true);
    try {
      await api.post("/bookings", {
        lead_id: leadId,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        date: format(date, "yyyy-MM-dd"),
        time_slot: slot,
        address: form.address.trim(),
        note: form.note.trim(),
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || "Rezerváciu sa nepodarilo odoslať. Skúste to znova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ink min-h-screen">
      <Header />
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-32 pb-24" data-testid="rezervacia-page">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm mb-8 font-mono2 uppercase tracking-widest" data-testid="rezervacia-back">
          <ArrowLeft className="w-4 h-4" /> Späť na hlavnú
        </Link>

        {done ? (
          <div className="max-w-xl border border-gold/30 bg-ink-2 p-10 text-center" data-testid="rezervacia-success">
            <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-display text-4xl text-white">Rezervácia prijatá</h1>
            <p className="text-white/60 mt-4 leading-relaxed">
              Ďakujeme. Termín obhliadky sme prijali a čoskoro vám ho telefonicky potvrdíme.
              Potvrdenie sme poslali aj na <span className="text-gold">{form.email}</span>.
            </p>
            <Link to="/" className="inline-block mt-8 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors" data-testid="rezervacia-home">
              Späť na hlavnú stránku
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Bezplatná obhliadka</p>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] text-white">
              Rezervujte si termín<br /><span className="text-gold">obhliadky zdarma</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Vyberte si deň a čas, ktorý vám vyhovuje. Obhliadka aj presná cenová ponuka sú
              zdarma a nezáväzné. Ozveme sa vám a termín potvrdíme.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
              {/* Left: date + slots */}
              <div className="space-y-8">
                <div className="border border-white/10 bg-ink-2 p-6">
                  <div className="flex items-center gap-2 text-white font-semibold mb-4">
                    <CalendarDays className="w-5 h-5 text-gold" /> Vyberte dátum
                  </div>
                  <div className="flex justify-center" data-testid="rezervacia-calendar">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      locale={sk}
                      formatters={{ formatCaption: (d) => { const s = format(d, "LLLL yyyy", { locale: sk }); return s.charAt(0).toUpperCase() + s.slice(1); } }}
                      disabled={[{ before: today }, ...blockedDateObjs]}
                      classNames={{
                        caption_label: "text-sm font-medium text-white capitalize",
                        head_cell: "text-white/40 rounded-md w-9 font-normal text-[0.8rem]",
                        day: "h-9 w-9 p-0 font-normal text-white/80 hover:bg-white/10 rounded-md aria-selected:opacity-100",
                        day_selected: "bg-gold text-ink hover:bg-gold focus:bg-gold font-bold",
                        day_today: "border border-gold/40 text-gold",
                        day_disabled: "text-white/20 opacity-40",
                        nav_button: "h-7 w-7 bg-transparent p-0 text-white/60 hover:text-gold border border-white/10 rounded-md",
                      }}
                    />
                  </div>
                </div>

                <div className="border border-white/10 bg-ink-2 p-6">
                  <div className="flex items-center gap-2 text-white font-semibold mb-4">
                    <Clock className="w-5 h-5 text-gold" /> Časový úsek
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {SLOTS.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => setSlot(s.key)}
                        data-testid={`slot-${s.key}`}
                        className={`p-4 border text-left transition-colors ${slot === s.key ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/30"}`}
                      >
                        <div className={`font-semibold text-sm ${slot === s.key ? "text-gold" : "text-white"}`}>{s.label}</div>
                        <div className="text-white/40 text-xs mt-1 font-mono2">{s.time}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: contact form */}
              <div className="border border-white/10 bg-ink-2 p-6 space-y-4">
                <div className="text-white font-semibold mb-2">Vaše údaje</div>
                <Field label="Meno a priezvisko *" value={form.name} onChange={(v) => set("name", v)} testid="rezervacia-name" />
                <Field label="E-mail *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="rezervacia-email" />
                <Field label="Telefón" value={form.phone} onChange={(v) => set("phone", v)} testid="rezervacia-phone" />
                <Field label="Adresa objektu" value={form.address} onChange={(v) => set("address", v)} testid="rezervacia-address" />
                <div>
                  <label className="text-white/50 text-xs font-mono2 uppercase tracking-widest">Poznámka</label>
                  <textarea
                    value={form.note}
                    onChange={(e) => set("note", e.target.value)}
                    rows={3}
                    data-testid="rezervacia-note"
                    className="mt-1 w-full bg-ink border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none resize-none"
                  />
                </div>

                {date && slot && (
                  <div className="text-sm text-white/60 border-t border-white/10 pt-4">
                    Vybraný termín: <span className="text-gold font-semibold">{format(date, "d. MMMM yyyy", { locale: sk })}</span>
                    {" · "}{SLOTS.find((s) => s.key === slot)?.time}
                  </div>
                )}

                <GdprConsent checked={gdpr} onChange={setGdpr} testid="rezervacia-gdpr" />

                <button
                  onClick={submit}
                  disabled={sending}
                  data-testid="rezervacia-submit"
                  className="w-full bg-gold text-ink py-4 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending ? "Odosielam…" : "Rezervovať obhliadku"}
                </button>
                <p className="text-white/35 text-xs text-center">Nezáväzné · Bezplatné · Bez skrytých poplatkov</p>
              </div>
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-white/50 text-xs font-mono2 uppercase tracking-widest">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-ink border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none"
      />
    </div>
  );
}
